import type {ReactElement} from 'react';
import React from 'react';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import type {ColorStorage} from '@Components/color-picker-v2';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor, getFillTypeFromColorFillType, getRGBAArrayFromColorStorage} from '@Utils/color.util';
import type {ColorFillPropertyType} from '@Components/color-fill';
import {PosterEditingBaseColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-color-panel';
import {useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem';

export function TranscriptActiveWordTextColorPanel(): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptActiveWordTextColorPanel');
  }

  const fillType = useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem('fillType', true);
  if (fillType === undefined) {
    throw new Error('Transcript item subtitle objects do not have fillType property in their activeWordTextStyles object');
  }

  const fillColor = useGetKeyFromFillOfSubtitleTextStylesForTranscriptItem('fillColor', true);
  if (fillColor === undefined) {
    throw new Error('Transcript item subtitle objects do not have fillColor property in their activeWordTextStyles object');
  }

  const onActiveWordTextColorChange = (updatedColor: ColorStorage[]): void => {
    void activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        activeWordTextStyles: {
          fill: {
            fillColor: getRGBAArrayFromColorStorage(updatedColor),
          },
        },
      },
      {undoable: false}
    );
  };

  const onActiveWordTextColorChangeEnded = (updatedColor: ColorStorage[]): void => {
    void activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      activeWordTextStyles: {
        fill: {
          fillColor: getRGBAArrayFromColorStorage(updatedColor),
        },
      },
    });
  };

  const onActiveWordTextColorTypeChanged = (newType: ColorFillPropertyType): void => {
    if (getFillTypeFromColorFillType(newType) === fillType) {
      return;
    }

    void activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      activeWordTextStyles: {
        fill: {
          fillType: getFillTypeFromColorFillType(newType),
          fillColor: activeItem.getAnySubtitle().activeWordTextStyles.fill.getColorForNewType(getFillTypeFromColorFillType(newType)),
        },
      },
    });
  };

  return (
    <PosterEditingBaseColorPanel
      id="transcript-active-word-text-fill-color-panel"
      color={getColorObjectArrayFromFillColor(fillColor)}
      onChangeColor={onActiveWordTextColorChange}
      onChangeEnded={onActiveWordTextColorChangeEnded}
      onChangeType={onActiveWordTextColorTypeChanged}
      isGradientEnabled
      type={getColorFillTypeFromFillType(fillType)}
    />
  );
}
